import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import {
  createPosition,
  listOfPermissions,
  updatePosition,
  viewPosition,
} from "Api/role";
import BaseCheckbox from "Components/Base/BaseCheckbox";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "Components/constants/Common";
import {
  cancelLabel,
  editRoleLabel,
  newRoleLabel,
  PermissionsKey,
  PermissionsLabel,
  RoleNameKey,
  RoleNameLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  InputPlaceHolder,
  validationMessages,
} from "../../Components/constants/validation";

interface PermissionData {
  id: number;
  parent_module_name: string;
  model_name: string;
}
interface FormValues {
  permissions: number[];
  role_name: string;
}

const AddRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  document.title = id ? editRoleLabel : newRoleLabel;
  const [loader, setLoader] = useState(false);
  const [viewRole, setViewRole] = useState<any>(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const roleNameRef = useRef<HTMLInputElement>(null);
  const permissionsRef = useRef<HTMLDivElement>(null);
  const [listOfPermissionData, setListOfPermissionData] = useState<
    PermissionData[]
  >([]);

  const groupByParentModule = (data: PermissionData[]) => {
    return data.reduce((acc: Record<string, PermissionData[]>, curr) => {
      if (!acc[curr.parent_module_name]) {
        acc[curr.parent_module_name] = [];
      }
      acc[curr.parent_module_name].push(curr);
      return acc;
    }, {});
  };

  const groupedPermissions = groupByParentModule(listOfPermissionData);

  const handlePermissionChange = (id: number, checked: boolean) => {
    if (checked) {
      roleFormik.setFieldValue(PermissionsKey, [
        ...roleFormik.values.permissions,
        id,
      ]);
    } else {
      roleFormik.setFieldValue(
        PermissionsKey,
        roleFormik.values.permissions.filter((perm: any) => perm !== id)
      );
    }
  };

  const handleParentChange = (parent: string, checked: boolean) => {
    const childPermissions = groupedPermissions[parent].map((item) => item.id);
    if (checked) {
      const newPermissions = Array.from(
        new Set([...roleFormik.values.permissions, ...childPermissions])
      );
      roleFormik.setFieldValue(PermissionsKey, newPermissions);
    } else {
      roleFormik.setFieldValue(
        PermissionsKey,
        roleFormik.values.permissions.filter(
          (perm: any) => !childPermissions.includes(perm)
        )
      );
    }
  };

  const isAllSelected = (parent: string) => {
    const childPermissions = groupedPermissions[parent].map((item) => item.id);
    return childPermissions.every((model) =>
      roleFormik.values.permissions.includes(model)
    );
  };

  const fetchViewRole = () => {
    if (id) {
      setLoader(true);
      viewPosition(Number(id))
        .then((response) => {
          if (checkStatusCodeSuccess(response.statusCode)) {
            const roleData = response.data;
            setViewRole(roleData);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  const roleFormik = useFormik<FormValues>({
    initialValues: {
      role_name: "",
      permissions: [],
    },
    validationSchema: yup.object({
      role_name: yup
        .string()
        .required(validationMessages.required(RoleNameLabel)),
      permissions: yup
        .array()
        .min(1, validationMessages.atLeastOneSelected(PermissionsLabel))
        .required(validationMessages.required(PermissionsLabel)),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      const payload = {
        role: values.role_name,
        permission_id: values.permissions,
      };

      if (id) {
        updatePosition(Number(id), payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/role");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        createPosition(payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/role");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });
  const fetchPermissions = () => {
    setLoader(true);
    listOfPermissions()
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCodes)) {
          setListOfPermissionData(response.data);
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (viewRole) {
      const rolePermissions = viewRole.authUserPermission.map(
        (perm: any) => perm.permission_id
      );
      roleFormik.setFieldValue(PermissionsKey, rolePermissions);
      roleFormik.setFieldValue(RoleNameKey, viewRole.role);
    }
  }, [viewRole]);

  useEffect(() => {
    if (roleFormik.errors.role_name && roleFormik.touched.role_name) {
      roleNameRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (
      roleFormik.errors.permissions &&
      roleFormik.touched.permissions
    ) {
      permissionsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [roleFormik.errors, roleFormik.touched]);

  useEffect(() => {
    fetchPermissions();
    if (id) {
      fetchViewRole();
    }
  }, [id]);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">{id ? editRoleLabel : newRoleLabel}</span>
          <button className="btn btn-danger" onClick={() => navigate("/role")}>
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap overflow-hidden">
          <div className="card-body">
            <form onSubmit={roleFormik.handleSubmit}>
              <div className="col-12 mt-3 list-group list-group-flush mx-4">
                <div className="mb-3 col-3" ref={roleNameRef}>
                  <BaseInput
                    name="role_name"
                    type="text"
                    label={RoleNameLabel}
                    className="shadow-none"
                    placeholder={InputPlaceHolder(RoleNameLabel)}
                    value={roleFormik.values.role_name}
                    touched={roleFormik.touched.role_name}
                    error={roleFormik.errors.role_name}
                    handleBlur={roleFormik.handleBlur}
                    onChange={roleFormik.handleChange}
                  />
                </div>
                <div className="me-5 p-1">
                  {Object.keys(groupedPermissions).map((parent, idx) => (
                    <div
                      key={idx}
                      className="border-bottom"
                      ref={permissionsRef}
                    >
                      <BaseCheckbox
                        id={`parent-${parent}`}
                        label={
                          <span className="capitalize label">{parent}</span>
                        }
                        checked={isAllSelected(parent)}
                        onChange={(e) =>
                          handleParentChange(parent, e.target.checked)
                        }
                        className="px-4 pe-4  py-2"
                        color="success"
                      />
                      <div className="d-flex flex-wrap">
                        {groupedPermissions[parent]?.length > 1 &&
                          groupedPermissions[parent].map((item) => (
                            <BaseCheckbox
                              key={item.id}
                              id={`permission-${item.id}`}
                              label={item.model_name}
                              checked={roleFormik.values.permissions.includes(
                                item.id
                              )}
                              onChange={(e) =>
                                handlePermissionChange(
                                  item.id,
                                  e.target.checked
                                )
                              }
                              className="px-4 pe-3"
                              labelClassName="font-weight-normal"
                              color="success"
                            />
                          ))}
                      </div>
                    </div>
                  ))}
                  {roleFormik.errors.permissions &&
                    roleFormik.touched.permissions && (
                      <span className="text-danger fs-7">
                        {roleFormik.errors.permissions}
                      </span>
                    )}
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2 mt-2">
                <BaseButton
                  color="success"
                  loader={btnLoader}
                  disable={btnLoader}
                  children={id ? updateLabel : submitLabel}
                  type="submit"
                />
                <BaseButton
                  color="danger"
                  children={cancelLabel}
                  type="button"
                  onClick={() => navigate("/role")}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRole;
